/**
 * Feuille du mode mobile, écrite à la main : dérogation assumée à l'extraction mécanique des
 * feuilles (`README.md:106-117`, `description.md:21`), la maquette n'ayant jamais eu de mobile.
 *
 * Tout est `!important` : la feuille vit dans le PREMIER `<style>` (`landing.tsx:75`), seul porteur
 * modifiable sans casser `landing.test.tsx:167-196`. Elle doit battre les feuilles extraites,
 * chargées après elle, et les styles inline posés en JS (`scene-slot.tsx:117`,
 * `scene-engine.ts:105,187,192`, `cockpit.anim.ts:154,235-236`, `coulisses.anim.ts:134,207-209`,
 * `final-height.ts:55`).
 *
 * La feuille porte aussi l'ouverture du cockpit (famille O : piste `sticky`, note à taille fixe, accrochée sous le titre par `--ov-ny` (O7n),
 * fondu des textes et de la synthèse pendant le décollage (O11, `--ov-co`), variables `--ov-*` écrites par
 * `scenes/cockpit.opening.ts`, replis = état `t = 0` sauf O11, dont le repli 1 est l'état sans JS de la passe 5)
 * et les cartouches (famille V).
 *
 * Elle porte aussi le cadrage (famille K, générée depuis `SECTIONS` : pour chaque cadrage, un porteur (variables
 * `--cad-*` écrites par `engine/cadrage-bind.ts`, réserve `::after`), une boîte collante et une portée ; sans
 * `frame` : `#<id>>.pin`, `#<id>>.pin>.in`, `#<id>` ; avec `frame` : `#<id> <holder>`, `#<id> <holder>><box>`, le
 * porteur). Modèle « synthèse » (avec textes : cockpit, risques, assistant, carte) : respiration partagée à parts
 * égales entre la marge haute des textes et celle de la synthèse (K3, textes centrés), synthèse à la taille des titres
 * de scène (K4), synthèse translatée par K5s de la hauteur libérée par la barre du navigateur (A) ou d'un décalage
 * fixe (B), textes de la moitié par K5t, selon `CADRAGE_FOLLOW_BAR`. Modèle « figure centrée » (sans textes, figures
 * 02-04) : demi-respiration au-dessus de la figure (K2) et entre la figure et le bloc (K3), K5s sur le bloc, K5f sur
 * la figure, C11 sur le calque de FIG 02. Elle porte enfin l'écran titre des coulisses (C1 : palier centré `sticky` et `--rush-y` ; C1s : porteur et retrait ; C1k : réserve ; `--rush-h` écrite par `engine/rush.ts`), le suiveur
 * de la figure 02 (C10 : `--rush-f`/`--rush-c`, écrites par `engine/rush.ts`), les écrans des figures (C3, C6-C9,
 * C11), les fenêtres 16/10 des démonstrations (D1c), la dernière page et le pied (famille F, dont F10 : flèche de suite masquée) et la typographie
 * téléphone (famille T, cran V4).
 *
 * Une seule `@media ${MOBILE_QUERY}` de premier niveau, donc inerte en mode ordinateur. Elle contient,
 * en avant-dernière entrée, une `@media` imbriquée pour le téléphone (T), puis, en dernière entrée,
 * une `@media (prefers-reduced-motion:reduce)` imbriquée (O10, dont C1sr, C1kr, C1r, C10r, C11r, O11r puis K5r).
 *
 * Contraintes d'écriture : `svh` partout ; `dvh` dans les seules bornes de K5t, K5s, K5f et C11 (une translation, jamais une
 * longueur de mise en page) ; jamais `vh`/`lvh` ; C1/C1s/C1k n'emploient que `svh` (centrage sur le petit viewport, R-9e) ; `100vw` est permis, c'est une largeur ;
 * aucun `overflow:hidden` (casserait `sticky`), pas de `@font-face`, pas de
 * `color-mix()` (bruit `[csstree-match]`, `styles/raw-import.test.ts:34-50`), pas de backtick, et
 * aucun commentaire CSS dans la chaîne (la traçabilité est portée par les commentaires TS).
 */
import {
	CADRAGE_FIXED_DROP,
	CADRAGE_FOLLOW_BAR,
	CADRAGE_GAP_BOTTOM,
	CADRAGE_GAP_TOP,
	CADRAGE_HOLD_SVH,
	CADRAGE_VARS,
} from "../engine/cadrage";
import { DEMO_HEIGHT_VAR, DEMO_SCALE_VAR, MOBILE_QUERY } from "../engine/env";
import { OPENING_SVH, OPENING_TITLE_K, OPENING_VARS } from "../engine/opening";
import { RUSH_FOLLOW_VARS, RUSH_HOLD_SVH, RUSH_TITLE_VAR, RUSH_VAR } from "../engine/rush";
import { SECTIONS, type CadrageFrame } from "../scene-manifest";

/** Règle plate canonique : `sel{d1!important;d2!important}`. */
const rule = (sel: string, decls: readonly string[]): string =>
	`${sel}{${decls.map((d) => `${d}!important`).join(";")}}`;

const demos = SECTIONS.flatMap((s) =>
	s.kind === "pin" && s.demo
		? [{ sel: `#${s.id} ${s.demo.target}`, w: s.demo.widthPx, pane: `#${s.id} ${s.demo.pane}` }]
		: [],
);

const cadrages = SECTIONS.flatMap((s) => (s.kind === "pin" && s.cadrage ? [{ id: s.id, ...s.cadrage }] : []));

// Repli de --hdrh = hauteur réelle de l'en-tête (.hdr .in{height:60px}, header.css:59) : le repli 56px
// (commons.css:14) décalait la page de 4px à l'arrivée du JS, visible depuis que le titre est peint au SSR.
const HDRH = "var(--hdrh,60px)";
const HEAD_GAP = 40; // marge sous l'en-tête (P6, P15, O5)
// Écart entre deux écrans des coulisses (titre → figure 02, figure → figure suivante) = marge haute d'une scène (P6) :
// 100 px > débord du bloc translaté par K5s (≤ ≈ 56 px) + CADRAGE_GAP_BOTTOM : l'écran suivant n'affleure ni pendant
// un palier ni à côté du titre seul (plan-figures R-7g, gardé par S28).
const SCREEN_GAP = `calc(${HDRH} + ${HEAD_GAP}px)`;
// Écart minimal dessin → bloc des figures 02-04 : .figtxt{margin-top:12px} de coulisses.css:6, que K3 remplace ;
// recopié ici (gardé par S29), compté dans --cad-h donc dans la règle « tient » ; en repli, le bloc reste à 12 px.
const FIG_TXT_GAP = 12;
// Gouttière latérale de .pin .in (commons.css:15), gardée par S31 : une légende tient dans 100vw − 2 × PAGE_PAD.
const PAGE_PAD = 16;
// Fenêtre 16/10 commune des démonstrations (description.md:41) = fenêtre du produit sur ordinateur (hero.css:55, gardé
// par S30). Hauteur explicite --demo-w / DEMO_RATIO (D1c) : aspect-ratio rognerait les fenêtres overflow:hidden (taille
// minimale automatique nulle d'un conteneur de défilement, plan-ecrans §3).
const DEMO_RATIO = 1.6;
// Légendes FIGURE 01-07 d'une ligne (description.md:42) : taille commune calée sur la largeur. CAP_FIT = 49 caractères
// (FIGURE 06, la plus longue) × (0,6 em de chasse Geist Mono + 0,1 em d'interlettrage) + 0,6 % de marge (gardé par S31) ;
// plancher CAP_MIN (sous ≈ 341 px de large, la plus longue repasse sur deux lignes) ; plafond CAP_MAX = taille d'origine
// (coulisses.css:11). En vw : ne suit pas le zoom du texte (R-8h).
const CAP_FIT = 34.5;
const CAP_MIN = 9;
const CAP_MAX = 11.5;
const CAP_TRACK = ".1em"; // interlettrage resserré (.15em dans coulisses.css:11)
const CAP_SIZE = `clamp(${CAP_MIN}px, (100vw - ${2 * PAGE_PAD}px) / ${CAP_FIT}, ${CAP_MAX}px)`;
// Dernière page (famille F) : « Commencer » aligné comme les portes (arbitrage A4, description.md:45 ne précise pas) ;
// tailles du pied plus petites que cta-footer.css:43,84 (13 et 11 px ; arbitrage A5). Réglages uniques, à juger sur appareil.
const FINAL_ALIGN = "left";
const FOOT_COPY_PX = 11;
const FOOT_LEGAL_PX = 10;
// Suiveur de la figure 02 (C10, C10r) et calque qui porte son dessin réel (C11, C11r ; la .figfig de FIG 02 est vide,
// scenes/coulisses.tsx:115-121).
const FOLLOWER = "#cl-grid>.figslot:first-child";
const FIG2_LAYER = "#coulisses .figslot>.figbox>.fig1-ov";
const TITLE_FIT = 4.85; // « Concevez. » = 4,794 em en Geist 590, -.048em (plan §2 bis) + 1,2 % de marge
const TITLE_MAX_SVH = 22; // plafond du titre plein écran (téléphone en paysage, R-O13)
const L2_RATIO = 26 / 56; // rapport .l2/.l1 aux planchers téléphone (hero.css:13-14)
const L2_GAP_EM = 10 / 26; // marge .l2 (hero.css:14), en em de .l2
const SSR_PUSH = "100svh"; // poussée de la grille avant le JS : contenu sous le titre hors champ
const K0 = OPENING_TITLE_K;
const V = OPENING_VARS;
// Bloc « titre + note » de l'ouverture (description.md:14, plan-cadrage-2 §5 point 1). Valeurs recopiées
// des feuilles extraites (gardées par S20) ; mesures Chrome sans interface, pas sur appareil.
const TITLE_LH = 0.98; // interligne de h1 (hero.css:12)
const NOTE_GAP = 16; // marge titre → note (hero.css:27)
const NOTE_PX = 13.5; // plancher de clamp(13.5px,1.05vw,17px) (hero.css:27), seul actif en mobile (1.05vw ≤ 10,84 px sous 1 032 px)
const NOTE_LH = 1.6; // interligne hérité de body (base.css:87), rendu explicite par O7n
// Note = 32 caractères × 0,6 em = 19,2 em (chasse fixe de Geist Mono) + 1 % de marge, comme TITLE_FIT ;
// dépend de la chasse de la fonte (R-A6), couverture gardée par S18.
const NOTE_FIT = 19.4;
const SCENE_TITLE_SIZE = "clamp(29px,3.7vw,56px)"; // titres de scène : h2.sec (commons.css:4), gardée par S19
// Repli t = 0 sans JS : TITLE_SIZE = corps final de .l1 (O6b, O6c) ; H1_H = hauteur de h1 (.l1, marge et
// ligne de .l2, mêmes arrondis qu'O6c ; exacte à 0,01 px dans Chrome) ; NOTE_F = corps de la note, à taille
// fixe (jamais agrandie : 13,5 px dès 294 px de large, R-N8) ; BLOCK0 = hauteur visuelle du bloc à t = 0
// (seul le titre est agrandi de K0) ; TY0 = translation du titre qui centre ce bloc sous l'en-tête et compense SSR_PUSH.
const TITLE_SIZE = `min(100cqw / ${TITLE_FIT}, ${TITLE_MAX_SVH}svh) / ${K0}`;
const H1_H = `(${TITLE_SIZE}) * ${(TITLE_LH + Number(L2_RATIO.toFixed(4)) * (Number(L2_GAP_EM.toFixed(4)) + TITLE_LH)).toFixed(4)}`;
const NOTE_F = `min(${NOTE_PX}px, 100cqw / ${NOTE_FIT})`;
const BLOCK0 = `${K0} * ${H1_H} + ${NOTE_GAP}px + ${NOTE_LH} * ${NOTE_F}`;
const TY0 = `max(-${HEAD_GAP}px, (100svh - ${HDRH}) / 2 - ${HEAD_GAP}px - (${BLOCK0}) / 2) - ${SSR_PUSH}`;
/** Bloc @media imbriqué : ne réintroduit pas de @media de premier niveau (S1). */
const nested = (cond: string, inner: readonly string[]): string => `@media ${cond}{${inner.join("\n")}}`;

// Typographie téléphone (famille T) : téléphones en portrait (≤ 440 px) et fenêtres étroites,
// téléphones en paysage ; l'iPad mini en portrait fait 744 px et garde P10 (description.md:22).
// Chaque valeur est un réglage à ajuster après test sur appareil (R-C3) ; aucune n'est mesurée sur appareil.
// Cran V4 (description.md:36, plan-ecrans §4 bis point 3, arbitrage A1) : une seule taille de texte pour les sept
// écrans à palier ; l'iPad vertical garde P10 (A3).
const PHONE_QUERY = "(max-width: 743.98px), (orientation: landscape)";
const PHONE_GRID_GAP = 16; // gap des grilles (24 en P7) ; invariant CADRAGE_GAP_TOP + 4 ≤ gap (R-C7, S15)
/**
 * Écart entre textes (26 en P10). Repli décidé d'avance R-8a : 8 → 6 si la carte n'a pas de palier sur un iPhone 393
 * (vérification appareil n° 9), aucune autre baisse. Le repli 8 → 6 n'est PAS appliqué.
 */
const PHONE_STEPS_GAP = 8;
const PHONE_H3 = 14; // titres d'étape
const PHONE_H3_LH = 1.25; // inchangé
const PHONE_P = 12; // paragraphes (.step p, #coulisses .figtxt p) ; HIG Caption 1
const PHONE_P_LH = 1.35; // interligne des paragraphes
const PHONE_P_GAP = 1; // marge titre → paragraphe, en px
const PHONE_CAP_GAP = 8; // FIGURE 01 → 8 px visuels sous la figure avec le gap de 16 (T2 : margin-top:-8px)
// Palier (famille K) : repli de --cad-h = pas de mesure, donc pas de palier ; pente de la marche K1 :
// 1 px de marge ouvre tout le palier (1000 px ≥ 33svh sur tout écran de moins de 3 000 px de haut).
const CAD_NO_MEASURE = "100000px";
const CAD_FIT_STEP = 1000;
// LIBRE (plan-cadrage §5 point 2) : place restante dans le petit viewport une fois le cadrage posé, en CSS
// (100svh vivant), jamais en JS ; respiration totale, partagée à parts égales par K3 (centrage, description.md:20).
const CAD_FREE = `100svh - ${HDRH} - ${CADRAGE_GAP_TOP + CADRAGE_GAP_BOTTOM}px - var(${CADRAGE_VARS.h},${CAD_NO_MEASURE})`;
// Demi-respiration (K2 et K3 de la figure centrée ; K3 des textes et de la synthèse) ; nulle quand le cadrage ne tient pas
// (LIBRE < 0) ou sans mesure. --cad-h exclut les deux moitiés (engine/cadrage.ts) : LIBRE ne boucle pas.
const CAD_HALF_FREE = `max(0px, calc((${CAD_FREE}) / 2))`;
// Marche « tient » partagée par K1 et K5 : ≥ 1000 px dès que LIBRE ≥ 0 (au-delà de toute borne), ≤ 0 quand le
// cadrage ne tient pas ou sans mesure (--cad-h absente) ; entre −1 et 0 px de LIBRE, borne rognée (tolérance d'arrondi).
const CAD_FITS = `calc((${CAD_FREE} + 1px) * ${CAD_FIT_STEP})`;
// K5, option A : hauteur libérée par la barre du navigateur, 0 quand elle est agrandie ou sans barre dynamique
// (dvh = svh). Seul emploi de dvh de la feuille (bornes de K5t et K5s), dans une translation : jamais en mise en page (WebKit 266835).
const CAD_BAR_SHIFT = "calc(100dvh - 100svh)";
// K5, option A : durée du glissement qui lisse la marche d'escalier de dvh (R-N1, R-N2) ; réglage à juger sur appareil.
const CAD_GLIDE_MS = 250;
const TITLE_SLOT = "#coulisses .chslot"; // porteur du titre (scenes/coulisses.tsx), bloc conteneur du sticky de #ch-head
const TITLE_FREE = `100svh - ${HDRH} - var(${RUSH_TITLE_VAR},${CAD_NO_MEASURE})`; // blanc autour du titre ; repli : pas de palier
const TITLE_HALF = `max(0px, calc((${TITLE_FREE}) / 2))`; // Δc
const TITLE_FITS = `calc((${TITLE_FREE} + 1px) * ${CAD_FIT_STEP})`; // marche « tient » (comme CAD_FITS)
const TITLE_APPROACH = `max(0px, ${TITLE_HALF} - ${SCREEN_GAP})`; // A
const TITLE_HOLD = `clamp(0px, ${TITLE_FITS}, ${TITLE_APPROACH} + ${RUSH_HOLD_SVH}svh)`; // R = A + Hp si le titre tient, 0 sinon

/**
 * Déclarations de K5t/K5s (sans `!important`) selon la bascule A/B (`engine/cadrage.ts`) : synthèse translatée de
 * la borne, textes de la moitié, même transition (les textes restent centrés pendant et après le glissement).
 * Borne basse 0 et marche `CAD_FITS` (celle de K1) : translations nulles quand le cadrage ne tient pas ou sans mesure.
 * A : borne = hauteur libérée par la barre, avec glissement. B : borne fixe (`CADRAGE_FIXED_DROP`), sans glissement.
 * Exportée pour que les tests couvrent les deux valeurs sans épingler la bascule.
 * @param followBar `CADRAGE_FOLLOW_BAR` en production
 * @returns `texts` et `synthesis` : `[transform, transition]` chacun en A, `[transform]` en B
 */
export function cadrageGroupDecls(followBar: boolean): {
	readonly texts: readonly string[];
	readonly synthesis: readonly string[];
} {
	const bound = followBar ? CAD_BAR_SHIFT : `${CADRAGE_FIXED_DROP}px`;
	const shift = `clamp(0px, ${CAD_FITS}, ${bound})`;
	const glide: readonly string[] = followBar ? [`transition:transform ${CAD_GLIDE_MS}ms ease-out`] : [];
	return {
		texts: [`transform:translateY(calc(${shift} / 2))`, ...glide],
		synthesis: [`transform:translateY(${shift})`, ...glide],
	};
}

/**
 * Sélecteurs d'un cadrage (plan-figures §5.B point 3) : porteur (variables --cad-*, réserve K1), boîte collante (K2,
 * K5) et portée (K3, K4). Sans `frame` : chaînes de la passe 6, sortie du cockpit inchangée à l'octet près. Avec
 * `frame` : `#id <holder>`, `#id <holder>><box>`, portée = porteur ; les règles valent pour chaque porteur, qui lit
 * son propre --cad-h par héritage.
 */
function cadrageSelectors(c: { readonly id: string; readonly frame?: CadrageFrame }): {
	readonly holder: string;
	readonly box: string;
	readonly scope: string;
} {
	if (!c.frame) return { holder: `#${c.id}>.pin`, box: `#${c.id}>.pin>.in`, scope: `#${c.id}` };
	const holder = `#${c.id} ${c.frame.holder}`;
	return { holder, box: `${holder}>${c.frame.box}`, scope: holder };
}

const rules: readonly string[] = [
	// P1 — snap.css:5 ; teinte Safari (Q2 A)
	rule("html", ["scroll-snap-type:none", "background-color:var(--paper)"]),
	// P2 — hauteurs inline (scene-slot.tsx:117, scene-engine.ts:105,187,192), snap.css:6
	rule(".pin-outer", [
		"height:auto",
		"scroll-snap-align:none",
		"scroll-snap-stop:normal",
		"overflow-x:clip",
	]),
	// P3 — section paresseuse pas encore montée (scene-slot.tsx:94-99)
	rule(".pin-outer:not(:has(> .pin))", ["min-height:100svh"]),
	// P4
	rule(".snap-end", ["display:none"]),
	// P5
	rule("footer", ["scroll-snap-align:none", "padding-bottom:32px"]),
	// P6 — commons.css:14, repli.css:21 ; repli --hdrh aligné sur header.css:59
	rule(".pin", [
		"position:static",
		"height:auto",
		"display:block",
		"overflow:visible",
		`padding:${SCREEN_GAP} 0 0`,
	]),
	// P7 — cockpit.css:3, risques.css:13, assistant.css:11, carte.css:10, repli.css:8
	rule("#cockpit .grid,#risques .grid,#assistant .grid,#carte .grid", [
		"display:flex",
		"flex-direction:column",
		"align-items:stretch",
		"gap:24px",
	]),
	// P8 — hero.css:3,47, coulisses.css:42
	rule(".hleft,#acte-block,.pl-left", ["display:contents"]),
	rule("#acte-block", ["pointer-events:auto"]),
	// P9 — coulisses.css:50-51, marge inline cockpit.tsx:107 (P9b : order:2 porté par D1a) ; titre de scène devenu
	// synthèse, en bas (description.md:35) : démo (2) → textes (3) → synthèse (5), ordre exigé par cadrageMetrics
	// (engine/cadrage.ts:121) ; O4 reste pour la sortie du cockpit
	rule(".grid h2.sec", ["order:5", "margin-top:0"]),
	rule(".grid .steps", ["order:3", "margin-top:0", "padding-top:0"]),
	// P10 — commons.css:26,30,32,34,42-48
	rule(".step", ["opacity:1"]),
	rule(".steps", ["gap:26px"]),
	rule(".step h3", ["font-size:18px"]),
	rule(".step p", ["font-size:14.5px"]),
	// P11 — repli.css:11 ; cartouches rendus (V1-V3, description.md:7)
	rule("#scroll-cue", ["display:none"]),
	// P12 — hero.css:11, dock cockpit.anim.ts:154 ; conteneur de requête du titre (O6b/O6c)
	rule("#hero-block", ["position:static", "inset:auto", "margin-left:0", "container-type:inline-size"]),
	// P13 — header.css:16,33-58
	rule(".hdr", ["background-color:var(--paper)"]),
	rule(".hdr .hblur", ["display:none"]),
	// P14 — base.css:89
	rule("body::after", ["display:none"]),
	// P15 — final-height.ts:54-55 ; dernière page (description.md:45) : en fin de page, « Commencer » à en-tête +
	// CADRAGE_GAP_TOP (min-height = 100svh − pied − en-tête − 12 + écart, --fth : engine/final-height.ts:57-59) ;
	// écart d'écran SCREEN_GAP au-dessus ; alignement FINAL_ALIGN (A4)
	rule("#final", [
		`min-height:calc(100svh - var(--fth,50px) - ${HDRH} - ${CADRAGE_GAP_TOP}px + ${SCREEN_GAP})`,
		`padding-top:${SCREEN_GAP}`,
		`text-align:${FINAL_ALIGN}`,
	]),
	// F1 — colonne pleine largeur : annule margin:0 auto de .wrap (base.css:94) dans #final en flex colonne (cta-footer.css:7-8)
	rule("#final .wrap", ["width:100%"]),
	// F2
	rule("#final h2", ["line-height:1.05"]),
	// F3-F6 — portes en colonne, sur la marge gauche, pleine largeur (description.md:46) : reprend cta-footer.css:36
	// (≤ 940 px) pour 941-1032 px en portrait, retrait latéral nul
	rule("#final .doors", ["grid-template-columns:1fr"]),
	rule("#final .door", ["padding:22px 0"]),
	rule("#final .door+.door", ["border-left:none", "border-top:1px solid var(--line)"]),
	rule("#final .door .ds", ["padding-top:16px", "justify-content:flex-start"]),
	// F7-F9 — pied sur une ligne (description.md:47) : © à gauche, mentions à droite, plus petits ; placement explicite en
	// grille sur ces deux seuls nœuds : juste que les boutons de soutien soient là ou non (brief-ecrans.md:21, R-8j)
	rule("footer .in", ["display:grid", "grid-template-columns:minmax(0,1fr) auto", "align-items:baseline", "gap:8px 12px"]),
	rule("footer .in>span", ["grid-area:1/1", `font-size:${FOOT_COPY_PX}px`]),
	rule("footer a.legal", ["grid-area:1/2", "position:static", "transform:none", `font-size:${FOOT_LEGAL_PX}px`]),
	// F10 — flèche de suite masquée en mobile, indépendamment du moteur
	rule("footer .next-cue", ["display:none"]),
	// O1 — piste de l'ouverture (description.md:13) : .pin-outer#cockpit allongée de OPENING_SVH
	rule("#cockpit::after", ["content:''", "display:block", `height:${OPENING_SVH}svh`]),
	// O2 — scène collée par le compositeur (R-O1) ; bat P6 par spécificité
	rule("#cockpit .pin", ["position:sticky", "top:0"]),
	// O3 — montée du bloc ; repli SSR : grille poussée hors champ
	rule("#cockpit .grid", [`transform:translateY(var(${V.gy},${SSR_PUSH}))`]),
	// O4 — cockpit : figure → cartouche → textes → synthèse (description.md:20)
	rule("#cockpit .grid h2.sec", ["order:5"]),
	rule("#cockpit .grid .steps", ["order:4"]),
	// O5 — titre : repli = état t = 0 (bloc visuel BLOCK0, titre agrandi de K0 et note à taille fixe, centré sous l'en-tête ; compense SSR_PUSH) ; hero.css:12
	rule("#hero-block h1", [
		"transform-origin:0 0",
		`transform:translateY(var(${V.ty},calc(${TY0}))) scale(var(${V.k},${K0}))`,
		"will-change:auto",
	]),
	// O6 — titre peint dès le rendu serveur : .rise annulé (hero.css:84-85)
	rule("#hero-block .l1,#hero-block .l2", ["opacity:1", "transform:none", "transition:none"]),
	// O6b/O6c — taille FINALE en mise en page (le grossissement K0 est un transform)
	rule("#hero-block .l1", [`font-size:calc(${TITLE_SIZE})`]),
	rule("#hero-block .l2", [
		`font-size:calc(${TITLE_SIZE} * ${L2_RATIO.toFixed(4)})`,
		`margin-top:${L2_GAP_EM.toFixed(4)}em`,
	]),
	// O7 — sous-titre, bouton : fondu et accroche sous le titre (même --ov-ny que la note, O7n) ; repli : invisibles
	rule("#hero-block .sub,#hero-block .ctas", [
		`opacity:var(${V.o},0)`,
		`transform:translateY(var(${V.ny},0px))`,
		"transition:none",
	]),
	rule("#hero-block .ctas", [`pointer-events:var(${V.pe},none)`]),
	// O7n — note à taille fixe (description.md:14-15) : opaque dès le rendu serveur, jamais mise à l'échelle,
	// accrochée sous le bas visuel du titre par --ov-ny (comme .sub et .ctas) ; repli = état t = 0 :
	// TY0 + (K0 − 1)·H1_H, sous le titre agrandi à son écart de mise en page (NOTE_GAP)
	rule("#hero-block .hnote", [
		"opacity:1",
		`font-size:${NOTE_F}`,
		`line-height:${NOTE_LH}`,
		`transform:translateY(var(${V.ny},calc(${TY0} + ${(K0 - 1).toFixed(2)} * ${H1_H})))`,
		"transition:none",
	]),
	// O8 — figure 1 : entrée par la droite, hors du scale (non réduite) ; bat D1a par spécificité
	rule("#cockpit .grid #cframe", [`transform:translateX(var(${V.fx},100vw)) scale(var(${DEMO_SCALE_VAR},1))`]),
	// O9 — FIGURE 01 hors cible réduite (coulisses.css:53, cockpit.anim.ts:325-327) : suit la figure
	rule("#cap-cockpit", ["position:static", "order:3", "margin-top:-12px", `transform:translateX(var(${V.fx},100vw))`]),
	// O11 — fondu du décollage (description.md:24) : textes et synthèse invisibles en fin d'ouverture, opaques à
	// l'arrivée sur le cadrage ; --ov-co écrite sur .grid par scenes/cockpit.opening.ts, héritée à travers
	// #acte-block (display:contents, P8) ; repli 1 : sans JS, rendu de la passe 5 (R-6h). Aucune transition : la
	// valeur suit l'image de défilement. Aucune autre règle ne pose opacity sur ces nœuds (P10 vise .step).
	rule("#cockpit .grid .steps,#cockpit .grid h2.sec", [`opacity:var(${V.co},1)`]),

	// C1 — écran titre (description.md:29) : palier centré entre l'en-tête et 100svh par sticky (top = en-tête + TITLE_HALF),
	// réserve C1k, retrait C1s ; monte de --rush-y en plus du défilement à partir de la fin du palier (origine décalée,
	// rushOrigin de engine/rush.ts, RUSH_K) ; replis : --rush-y 0px, --rush-h CAD_NO_MEASURE : sticky inerte, passe 8 ; écart
	// avec la synthèse = SCREEN_GAP (P6, description.md:37) ; bat .ch-head{position:relative} (charniere.css:6, z-index:2
	// conservé) et la translation en ligne du film ordinateur (coulisses.anim.ts:169). C1r : mouvement réduit.
	rule("#ch-head", [
		"position:sticky",
		`top:calc(${HDRH} + ${TITLE_HALF})`,
		`transform:translateY(var(${RUSH_VAR},0px))`,
		"margin-left:0",
	]),
	// C1s — porteur du titre : bloc (bat le display:contents en ligne, neutre sur ordinateur), retrait −A qui rend à la figure 02
	// la distance de la passe 8 à la fin du palier (sinon phase 3× réduite, R-9j). C1sr.
	rule(TITLE_SLOT, ["display:block", `margin-bottom:calc(-1 * ${TITLE_APPROACH})`]),
	// C1k — réserve du palier : approche A + RUSH_HOLD_SVH si le titre tient, 0 sinon (comme K1). C1kr.
	rule(`${TITLE_SLOT}::after`, ["content:''", "display:block", `height:${TITLE_HOLD}`]),
	// C1b — coulisses.anim.ts:248
	rule("#ch-sub", ["transform:none", "margin-left:0"]),
	// C2 — charniere.css:18,26, coulisses.css:62
	rule("#ch-srcs,#coulisses .vcaret,.figsep", ["display:none"]),
	// C3 — coulisses.anim.ts:208-209, coulisses.css:2 ; row-gap = écart entre écrans des figures (SCREEN_GAP, R-7g)
	rule("#cl-grid", [
		"--figh:min(420px,90vw)",
		"opacity:1",
		"transform:none",
		"grid-template-columns:1fr",
		`row-gap:${SCREEN_GAP}`,
	]),
	// C4 — coulisses.css:4,58, TOP=110 de coulisses-figures.ts:456
	rule("#cl-grid .figfig", ["height:var(--figh)"]),
	rule(".fig1-ov", ["height:calc(var(--figh) + 110px)"]),
	// C5 — coulisses.css:6-7,15-16
	rule("#coulisses .figtxt", ["opacity:1", "transform:none"]),
	rule("#coulisses .figtxt .fg,#coulisses .figtxt p", ["color:var(--ink)"]),
	// C6 — écart titre → figure 02 (SCREEN_GAP) ; bat charniere.css:27
	rule("#coulisses>.pin>.in", [`gap:${SCREEN_GAP}`]),
	// C7 — enveloppes d'écran (scenes/coulisses.tsx) en bloc : chaque porteur est le bloc conteneur du sticky de sa
	// boîte (K1, K2) ; bat le display:contents en ligne (neutre sur ordinateur)
	rule("#cl-grid .figslot,#cl-grid .figbox", ["display:block"]),
	// C8 — écart minimal dessin → bloc (FIG_TXT_GAP, coulisses.css:6), dans la mesure --cad-h
	rule("#cl-grid .figcol", [`gap:${FIG_TXT_GAP}px`]),
	// C9 — légende « FIGURE 0N : … » collée à son paragraphe, d'une ligne : taille commune CAP_SIZE (comme V2),
	// interlettrage CAP_TRACK, interligne 1,25
	rule("#coulisses .figtxt .fg", [`font-size:${CAP_SIZE}`, `letter-spacing:${CAP_TRACK}`, "line-height:1.25"]),
	// C10 — rattrapage de la figure 02 (description.md:29, plan-ecrans point 6) : --rush-f / --rush-c écrites par
	// engine/rush.ts (RUSH_FOLLOW_VARS). Terme A = 100svh − --rush-f (bas du petit viewport quand le titre est seul, puis
	// RUSH_FOLLOW_K), terme C = --rush-c (nul quand le haut du porteur atteint l'en-tête), jamais négative ; replis 100svh /
	// 0px : translation nulle sans JS. Aucun dvh (R-8d). Transforme le porteur d'une boîte collante (R-8e). C10r.
	rule(FOLLOWER, [
		`transform:translateY(max(0px, min(calc(100svh - var(${RUSH_FOLLOW_VARS.lead},100svh)), var(${RUSH_FOLLOW_VARS.cap},0px))))`,
	]),
	// C11 — figure centrée : le calque qui porte le dessin de FIG 02 glisse de la moitié comme K5f (même déclaration que
	// K5t/K5f) ; une règle dédiée, car un sélecteur figure combiné « .figfig,.fig1-ov » fausserait --cad-off. C11r.
	rule(FIG2_LAYER, cadrageGroupDecls(CADRAGE_FOLLOW_BAR).texts),
	// V1/V2 — cartouches (description.md:7) : style complet ici, sans dépendre du chunk coulisses.css
	// (coulisses.css:6-7,11,27,34) ; taille et marge divisées par --demo-s hérité de la cible réduite
	// (demo-fit.ts:34) → CAP_SIZE et 12 px à l'écran ; #cap-cockpit hors cible → --demo-s vaut 1
	rule(".viscap", [
		"display:block",
		"opacity:1",
		"transform:none",
		"order:2",
		"width:100%",
		"max-width:none",
		"text-align:left",
		`margin:calc(12px / var(${DEMO_SCALE_VAR},1)) 0 0`,
	]),
	// V2 — cartouche d'une ligne (description.md:42) : taille commune CAP_SIZE divisée par --demo-s hérité de la cible
	// réduite (même taille à l'écran que C9), interlettrage CAP_TRACK, line-height 1,25
	rule(".viscap .fg", [
		"font-family:var(--mono)",
		`font-size:calc(${CAP_SIZE} / var(${DEMO_SCALE_VAR},1))`,
		"line-height:1.25",
		`letter-spacing:${CAP_TRACK}`,
		"color:var(--ink-55)",
	]),
	// V3 — étages en colonne (repris de coulisses.css:35), cartouche sous l'image (order:2)
	rule("#rq-stage,#ce-stage", ["display:flex", "flex-direction:column"]),
	// K1-K5 — cadrage mobile (description.md:19-30), généré depuis SECTIONS comme D1a, par porteur / boîte / portée
	// (cadrageSelectors). Cinq cadrages : cockpit, coulisses, risques, assistant, carte.
	// K1 : réserve du palier dans le porteur ; marche : CADRAGE_HOLD_SVH si l'écran tient (LIBRE ≥ −1px), 0 sinon
	//      (défilement normal, description.md:58, repli description.md:72).
	// K2 : boîte collée quand la figure atteint en-tête + CADRAGE_GAP_TOP (--cad-off = décalage de la figure) ; figure
	//      centrée (sans textes) : + demi-respiration au-dessus.
	// K3 : demi-respiration (CAD_HALF_FREE) dans les deux modèles : avec textes, marge haute des textes et de la
	//      synthèse (textes centrés, description.md:20) ; figure centrée, marge du bloc (description.md:30). --cad-h
	//      exclut ces marges.
	// K4 (avec textes) : synthèse à la taille des titres de scène (description.md:21).
	// K5t (avec textes) / K5f (figure centrée) / K5s : translation de la hauteur libérée par la barre (A) ou de
	//      CADRAGE_FIXED_DROP (B) ; textes et figure de la moitié, synthèse entière. Sélecteurs de la boîte de K2,
	//      distincts de K3/K4 ; marche CAD_FITS.
	...cadrages.flatMap((c) => {
		const { holder, box, scope } = cadrageSelectors(c);
		const { figure, texts, synthesis } = c;
		const k5 = cadrageGroupDecls(CADRAGE_FOLLOW_BAR);
		const centered = texts === undefined ? ` + ${CAD_HALF_FREE}` : ""; // figure centrée : demi-respiration au-dessus
		return [
			rule(`${holder}::after`, [
				"content:''",
				"display:block",
				`height:clamp(0px, ${CAD_FITS}, ${CADRAGE_HOLD_SVH}svh)`,
			]),
			rule(box, [
				"position:sticky",
				`top:calc(${HDRH} + ${CADRAGE_GAP_TOP}px${centered} - var(${CADRAGE_VARS.off},0px))`,
			]),
			...(texts === undefined
				? [rule(`${scope} ${synthesis}`, [`margin-top:${CAD_HALF_FREE}`]), rule(`${box} ${figure}`, k5.texts)]
				: [
						rule(`${scope} ${texts},${scope} ${synthesis}`, [`margin-top:${CAD_HALF_FREE}`]),
						rule(`${scope} ${synthesis}`, [`font-size:${SCENE_TITLE_SIZE}`]),
						rule(`${box} ${texts}`, k5.texts),
					]),
			rule(`${box} ${synthesis}`, k5.synthesis),
		];
	}),

	// D1a — cockpit.anim.ts:236, hero.css:52 (généré depuis SECTIONS ; P9b : order:2)
	rule(demos.map((d) => d.sel).join(","), [
		"order:2",
		"width:var(--demo-w)",
		"max-width:none",
		"flex:none",
		"align-self:flex-start",
		"transform-origin:0 0",
		`transform:scale(var(${DEMO_SCALE_VAR},1))`,
		`margin:0 calc(var(--demo-w) * (var(${DEMO_SCALE_VAR},1) - 1)) calc(var(${DEMO_HEIGHT_VAR},0px) * (var(${DEMO_SCALE_VAR},1) - 1)) 0`,
		"will-change:auto",
	]),
	// D1b — largeur de conception de chaque démonstration
	...demos.map((d) => rule(d.sel, [`--demo-w:${d.w}px`])),
	// D1c — fenêtre 16/10 commune (description.md:41), générée depuis SECTIONS (DemoSpec.pane) : hauteur explicite
	// widthPx / DEMO_RATIO, --demo-w hérité de la cible (D1b). Contenu plus haut : widthPx relevée (dessin plus large),
	// jamais coupé (R-8f). D2 garde aspect-ratio:auto et max-height:none sur #cframe : la hauteur explicite décide seule.
	rule(demos.map((d) => d.pane).join(","), [`height:calc(var(--demo-w) / ${DEMO_RATIO})`]),
	// D2 — hero.css:52-55,66-68, cockpit.anim.ts:235
	rule("#cframe", ["opacity:1", "aspect-ratio:auto", "max-height:none"]),
	// D3 — cockpit.css:31,38, repli.css:9,12, hero.css:79
	rule(".ck-grid", [
		"grid-template-columns:repeat(12,minmax(0,1fr))",
		"grid-template-rows:auto auto",
		"grid-template-areas:'pa pa pa pj pj pj ca ca ca ca ca ca' 'co co co rg rg rg do do do ri ri ri'",
		"max-width:82%",
		"gap:10px",
	]),
	// pas de width : l'état ouvert serait écrasé
	rule(".ck-chat", ["display:flex"]),
	rule(".chat-open .ck-chat", ["width:calc(22% - 26px)"]),
	// D4 — repli.css:27-31, risques.css:28,80,83,86,93
	rule(".rq-grid", ["grid-template-columns:repeat(3,1fr)"]),
	rule(".rq-dlg", ["width:70%"]),
	rule(".rq-dlg.wide", ["width:86%"]),
	rule(".rq-ov", ["align-items:center"]),
	rule(".rq-carr", ["width:44cqw"]),
	rule("#rq-frame", ["will-change:auto"]),
	// D5 — repli.css:22-24, assistant.css:20,77,81 ; hauteur de .th-frame : D1c
	rule(".th-side", ["display:flex"]),
	rule(".bw-frame", ["display:flex", "height:calc(var(--demo-w) / 1.6)"]),
	rule(".bw-frame.on", ["width:calc((calc(var(--demo-w) / 1.6) - 192px) / 1.4143)", "margin-left:20.16px"]),
	// D6 — repli.css:32-33, carte.css:12,104 ; hauteur de .ce-frame : D1c
	rule(".ce-aside", ["display:flex"]),
	rule(".ce-frame", ["will-change:auto"]),
	// D7 — cockpit.css:6,7,27,59,60,62,72,79,96,102 (sans 3vw), repli.css:18-19, hero.css:73-83
	rule("#cockpit .ck-head", ["padding:18px 20px 4px 20px"]),
	rule("#cockpit .ck-body", ["padding:12px 16px 16px 16px"]),
	rule("#cockpit .ck-head h3", ["font-size:24px"]),
	rule("#cockpit .encart", ["padding:11px 13px 12px"]),
	rule("#cockpit .encart .eh", ["font-size:12.5px", "margin-bottom:6px"]),
	rule("#cockpit .erow", ["font-size:11.5px", "padding:4.5px 0"]),
	rule("#cockpit .rrow,#cockpit .thr", ["font-size:11.5px", "padding:3.5px 0"]),
	rule("#cockpit .rk", ["font-size:11.5px"]),
	rule("#cockpit .dtile .fic", ["width:34px", "height:34px"]),
	// D8 — valeurs de base de risques.css:26-131, annulent la compression tablette (66-77,137-142)
	rule("#risques .rq-page", ["padding:12px 14px 0"]),
	rule("#risques .rq-grid", ["gap:9px", "padding-bottom:10px"]),
	rule("#risques .rq-card", ["padding:9px 10px 8px", "gap:6px"]),
	rule("#risques .rq-card h4", ["font-size:11px"]),
	rule("#risques .rqb", ["font-size:8.5px", "padding:1.5px 6px"]),
	rule("#risques .rqb svg", ["width:9px", "height:9px"]),
	rule("#risques .rq-cbody", ["gap:7px"]),
	rule("#risques .rq-info", ["font-size:8.5px", "line-height:1.45", "gap:10px"]),
	rule("#risques .rq-src", ["font-size:7.5px", "padding-top:5px"]),
	rule("#risques .rq-disc", ["padding:7px 2px 9px", "font-size:7.5px"]),
	rule("#risques .rq-skel", ["padding:9px 10px"]),
	rule("#risques .rq-side", ["font-size:10px"]),
	rule("#risques .rqt-xs", ["font-size:7.5px"]),
	rule("#risques .rqt-sm", ["font-size:8.5px"]),
	rule("#risques .rqt-base", ["font-size:10px"]),
	rule("#risques .rqt-g1", ["gap:2.5px"]),
	rule("#risques .rqt-g15", ["gap:3.5px"]),
	rule("#risques .rqt-g2", ["gap:5px"]),
	rule("#risques .rqt-g3", ["gap:7.5px"]),
	rule("#risques .rqt-g4", ["gap:10px"]),
	rule("#risques .rqt-g6", ["gap:15px"]),
	rule("#risques .rqt-gx5", ["column-gap:12.5px"]),
	rule("#risques .rqt-gy2", ["row-gap:5px"]),
	rule("#risques .rqt-mt1", ["margin-top:2.5px"]),
	rule("#risques .rqt-mt2", ["margin-top:5px"]),
	rule("#risques .rqt-pt3", ["padding-top:7.5px"]),
	// T — typographie téléphone (description.md:22) : cran V4, une seule taille pour les sept écrans à palier ; hero exclu (R-C10) ;
	// placé avant O10, il bat les règles de même spécificité qui le précèdent (P7, P10, O9).
	nested(PHONE_QUERY, [
		rule("#cockpit .grid,#risques .grid,#assistant .grid,#carte .grid", [`gap:${PHONE_GRID_GAP}px`]),
		rule("#cap-cockpit", [`margin-top:-${PHONE_CAP_GAP}px`]),
		rule(".steps", [`gap:${PHONE_STEPS_GAP}px`]),
		rule(".step h3", [`font-size:${PHONE_H3}px`, `line-height:${PHONE_H3_LH}`]),
		rule(".step p", [`font-size:${PHONE_P}px`, `line-height:${PHONE_P_LH}`, `margin-top:${PHONE_P_GAP}px`]),
		rule("#coulisses .figtxt p", [`font-size:${PHONE_P}px`, `line-height:${PHONE_P_LH}`]), // 14 px dans coulisses.css:16, interligne 1,6 hérité de body (base.css:87) ; comme .step p
	]),
	// O10 — mouvement réduit (description.md:48) : disposition finale dès le rendu serveur, sans piste ; C1sr, C1kr, C1r, C10r, C11r, O11r puis K5r en dernier
	nested("(prefers-reduced-motion:reduce)", [
		rule("#cockpit::after", ["display:none"]),
		rule("#cockpit .pin", ["position:static"]),
		rule("#cockpit .grid", ["transform:none"]),
		rule("#hero-block h1", ["transform:none"]),
		rule("#hero-block .hnote", ["transform:none"]),
		rule("#hero-block .sub,#hero-block .ctas", ["opacity:1", "transform:none", "pointer-events:auto"]),
		rule("#cockpit .grid #cframe", [`transform:scale(var(${DEMO_SCALE_VAR},1))`]),
		rule("#cap-cockpit", ["transform:none"]),
		// C1sr, C1kr — mouvement réduit : ni retrait ni réserve du palier du titre (comportement actuel conservé, brief point 5, R-9g)
		rule(TITLE_SLOT, ["margin-bottom:0"]),
		rule(`${TITLE_SLOT}::after`, ["display:none"]),
		// C1r — mouvement réduit : écran titre sans palier ni accélération, quelle que soit --rush-y / --rush-h (R-7m, R-9g) ; même sélecteur que C1, placé après
		rule("#ch-head", ["transform:none", "position:static"]),
		// C10r — mouvement réduit : figure 02 à sa place de mise en page, quelles que soient --rush-f/--rush-c (R-8l)
		rule(FOLLOWER, ["transform:none"]),
		// C11r — glissement du calque de FIG 02 coupé, translation gardée (comme K5r, R-N15)
		rule(FIG2_LAYER, ["transition:none"]),
		// O11r — mouvement réduit : textes et synthèse visibles directement dans le cadrage (description.md:24), quelle
		// que soit --ov-co (R-6i : le JS peut l'avoir laissée à 0 si la préférence change en cours de session)
		rule("#cockpit .grid .steps,#cockpit .grid h2.sec", ["opacity:1"]),
		// K5r — miroirs des K5t (avec textes) ou K5f (figure centrée), puis K5s, même spécificité, placés après
		...cadrages.flatMap((c) => {
			const { box } = cadrageSelectors(c);
			return [
				rule(`${box} ${c.texts ?? c.figure}`, ["transition:none"]),
				rule(`${box} ${c.synthesis}`, ["transition:none"]),
			];
		}),
	]),
];

/** Feuille du mode mobile : une seule `@media` de premier niveau, sans blanc avant ni après. */
export const mobileCss: string = `@media ${MOBILE_QUERY}{${rules.join("\n")}}`;
